import { useState, useEffect, useRef, lazy, Suspense } from "react";
import { Routes, Route, Navigate, useParams, useLocation } from "react-router-dom";

import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min.js";
import "bootstrap-icons/font/bootstrap-icons.css";
import "./styles/stylesheet.css";

import { useFetchData } from "./utils/data/useFetchData.js";
import { resolvePath } from "./utils/nav/path";
import buildTheme from "./utils/buildTheme.js";

import MainNav from "./components/nav/MainNav.jsx";
import Home from "./pages/Home.jsx";
import LoginModal from "./components/nav/LoginModal.jsx";
import ProtectedRoute from "./components/nav/ProtectedRoute.jsx";
import IntroTutorial from "./components/IntroTutorial.jsx";

const ProjectDetail = lazy(() => import("./pages/ProjectDetail.jsx"));
const InternalProjectDetail = lazy(() => import("./pages/InternalProjectDetail.jsx"));
const DataSheet = lazy(() => import("./components/editor/DataSheet.jsx"));
const Detail = lazy(() => import("./components/templates/home/Detail.jsx"));

import { useLocale } from "./state/useLocale";
import { useTranslations } from "./state/useTranslations";
import { LocaleContext } from "./state/LocaleContext.js";

import { prepMapPayload } from "./utils/prepMapPayload.js";

export default function App() {
  // Helper to strip HTML tags and decode entities to plain text
  const stripHtml = (html) => {
    if (!html) return '';
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    const text = tmp.textContent || tmp.innerText || '';
    return text.replace(/\s+/g, ' ').trim();
  };

  // --- Data hooks (leave these first) ---
  const {
    data: pages,
    loading: loadingPages,
    error: errorPages,
  } = useFetchData("pages");

  const {
    data: groups,
    loading: loadingGroups,
    error: errorGroups,
  } = useFetchData("groups");

  const {
    data: statuses,
    loading: loadingStatuses,
    error: errorStatuses,
  } = useFetchData("status");

  const {
    data: systemPages,
    loading: loadingSystemPages,
    error: errorSystemPages,
  } = useFetchData("system_pages");

  const [mapData, setMapData] = useState(null);
  const [loadingMapData, setLoadingMapData] = useState(true);
  const [errorMapData, setErrorMapData] = useState(null);

  useEffect(() => {
    const fetchMapData = async () => {
      try {
        const res = await fetch("/api/map-data");
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const apiData = await res.json();

        // Use shared prepMapPayload to normalize the data
        const { settings, statuses, projects, categories, issues } =
          prepMapPayload(apiData);

        setMapData({ settings, statuses, projects, categories, issues });
      } catch (err) {
        console.error("[App] map-data fetch failed:", err);
        setErrorMapData(err.message);
      } finally {
        setLoadingMapData(false);
      }
    };

    fetchMapData();
  }, []);

  // --- Other state / params ---
  const [role, setRole] = useState(localStorage.getItem("role"));
  const [showLogin, setShowLogin] = useState(false);
  const { groupSlug, "*": slugPath } = useParams();
  const location = useLocation();

  // --- Ready check ---
  const isReady =
    !loadingPages &&
    !loadingGroups &&
    !loadingStatuses &&
    !loadingMapData &&
    !loadingSystemPages &&
    !errorPages &&
    !errorGroups &&
    !errorStatuses &&
    !errorMapData;

  // --- Group / page resolution ---
  // For ProjectDetail routes, use data from ProjectDetail component (loaded from API)
  // For other routes (Home, etc), use data from global App state
  // Helper to check if current route is a ProjectDetail page
  const pathSegments = location.pathname.split('/').filter(Boolean);
  const hasDetailPagePath = location.pathname !== '/' && pathSegments.length >= 2;

  const page = hasDetailPagePath && window._currentProjectDetailPage
    ? window._currentProjectDetailPage
    : (isReady && groupSlug && groups?.rows?.length && pages?.length
        ? resolvePath(groupSlug, slugPath, groups.rows, pages) || {}
        : {});

  const group = hasDetailPagePath && window._currentProjectDetailGroup
    ? window._currentProjectDetailGroup
    : (isReady && groupSlug
        ? groups.rows.find((g) => g.slug === groupSlug) || null
        : null);

  // Debug page resolution
  if (isReady && groupSlug) {
    console.log('[App] Page resolution:', {
      groupSlug,
      slugPath,
      foundGroup: !!group,
      foundPage: !!page?.id,
      pageTitle: page?.title,
      groupsCount: groups?.rows?.length,
      pagesCount: pages?.length
    });
  }

  // --- Map categories ---
  const categories = mapData?.categories || {};
  const settings = mapData?.settings || {};
  const mapStatuses = statuses?.rows || [];

  // Set CSS custom properties from theme
  useEffect(() => {
    const theme = buildTheme(settings?.theme?.primary, settings?.theme?.accent);
    const root = document.documentElement.style;
    root.setProperty("--theme-primary", theme.primary);
    root.setProperty("--theme-primary-dark", theme.primaryDark);
    root.setProperty("--theme-dark-bg", theme.darkBg);
    root.setProperty("--theme-card-bg", theme.cardBg);
    root.setProperty("--theme-border-dark", theme.borderDark);
    root.setProperty("--theme-accent", theme.accent);
    root.setProperty("--octane-link", theme.primary);
  }, [settings?.theme?.primary, settings?.theme?.accent]);

  // Set meta description from settings
  useEffect(() => {
    const cityName = settings?.city?.name;
    const desc = cityName
      ? `${cityName} capital improvement project tracker — view project status, locations, and details.`
      : "Municipal capital improvement project tracker — view project status, locations, and details.";
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", desc);
  }, [settings?.city?.name]);

  // --- Locale setup ---
  const { locale, setLocale } = useLocale();
  const translations = useTranslations(locale);
  const t = (entityType, entityId, fieldName, fallback) => {
    const key = `${entityType}:${entityId}:${fieldName}`;
    return translations[key] || fallback;
  };
  const tUI = (name, fallback) => t("ui", 0, name, fallback);

  // --- OM Context Integration ---

  // Initialize context on app load with baseline info
  useEffect(() => {
    // Import and expose OMContext and eventBus globally
    import("@om/collector-sdk").then(({ OMContext, eventBus }) => {
      window.OMContext = OMContext;
      window.eventBus = eventBus;

      // Get or generate session ID
      let sessionId = sessionStorage.getItem('om_session_id');
      if (!sessionId) {
        sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        sessionStorage.setItem('om_session_id', sessionId);
      }

      // Set app metadata
      try {
        OMContext.set('app', {
          name: 'casa-grande-project-tracker',
          version: '1.0.0',
          initialized: Date.now()
        });

        // Set initial context with baseline info
        const initialContext = {
          locale: locale || 'en',
          app: 'project-tracker',
          sessionId: sessionId,
          route: location.pathname,
          timestamp: Date.now()
        };

        OMContext.set('context', initialContext);
        OMContext.setAppStatus('tracker', 'ready');
        console.log('[OMContext] Tracker initialized with context:', initialContext);
      } catch (err) {
        console.warn('[OMContext] Failed to initialize:', err);
      }
    });
  }, []); // Only run once on mount

  // Update locale in OMContext when it changes (metadata only — does NOT
  // publish context:changed because locale is not a navigation event)
  useEffect(() => {
    if (locale && window.OMContext) {
      try {
        window.OMContext.updateContext('locale', locale);
      } catch (err) {
        console.warn('[OMContext] Failed to update locale:', err);
      }
    }
  }, [locale]);

  // --- Context updates: session start + deliberate navigation only ---
  // Never reacts to data settling, re-renders, or locale changes.
  // Map popup open/close is handled by MapView.jsx.
  // Detail page loads are handled by the projectDetailLoaded event below.
  const prevPathnameRef = useRef(null); // null = initial context not yet set

  useEffect(() => {
    if (!isReady || !window.OMContext || !window.eventBus) return;

    const isInitial = prevPathnameRef.current === null;
    const pathnameChanged = location.pathname !== prevPathnameRef.current;

    // Only act on (a) first run after data is ready, or (b) actual navigation
    if (!isInitial && !pathnameChanged) return;

    prevPathnameRef.current = location.pathname;

    // Detail pages: projectDetailLoaded event handles context
    const pathParts = location.pathname.split('/').filter(Boolean);
    if (location.pathname !== '/' && pathParts.length >= 2) return;

    // Home / map page
    const currentContext = window.OMContext.get('context') || {};
    const contextUpdate = {
      ...currentContext,
      route: location.pathname,
      timestamp: Date.now(),
      type: 'map',
      title: settings?.city?.name || 'Casa Grande',
      description: 'Project tracker'
    };

    window.OMContext.set('context', contextUpdate);
    window.eventBus.publish('context:changed', contextUpdate);
    console.log('[OMContext] Context set (initial=%s):', isInitial, contextUpdate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReady, location.pathname]);

  // Listen for ProjectDetail data loaded event
  useEffect(() => {
    const handleProjectDetailLoaded = (event) => {
      const { page: detailPage, group: detailGroup } = event.detail;
      console.log('[OMContext] ProjectDetail loaded, updating context:', { page: detailPage, group: detailGroup });

      try {
        const pageUpdate = {
          type: 'page',
          title: detailPage.title === 'Home' && detailGroup?.title
            ? `${detailGroup.title} ${detailPage.title}`
            : detailPage.title,
          description: stripHtml(detailPage.description) || '',
          route: location.pathname,
          timestamp: Date.now(),
          // Include structured page, group, and project data for avatar
          page: {
            id: detailPage.id,
            slug: detailPage.slug,
            title: detailPage.title
          },
          group: {
            id: detailGroup?.id,
            slug: detailGroup?.slug,
            title: detailGroup?.title
          },
          project: {
            id: detailPage.id, // Page ID serves as project ID in this context
            name: detailPage.title,
            slug: detailPage.slug
          }
        };

        window.OMContext.set('context', pageUpdate);
        window.eventBus.publish('context:changed', pageUpdate);
        console.log('[OMContext] Page selected, context updated:', pageUpdate);
      } catch (err) {
        console.warn('[OMContext] Failed to update context from ProjectDetail:', err);
      }
    };

    window.addEventListener('projectDetailLoaded', handleProjectDetailLoaded);
    return () => window.removeEventListener('projectDetailLoaded', handleProjectDetailLoaded);
  }, [location.pathname]);

  // --- JSX ---
  return (
    <LocaleContext.Provider value={{ locale, setLocale, translations, t, tUI }}>
      <div className="app-shell">
        <MainNav
          groups={groups}
          pages={pages}
          statuses={statuses}
          group={group}
          page={page}
          role={role}
          setRole={setRole}
          showLogin={showLogin}
          setShowLogin={setShowLogin}
          isReady={isReady}
          settings={settings}
        />

        <main>
          <Suspense fallback={null}>
          <Routes>
            <Route
              index
              element={
                <Home
                  key={loadingMapData ? "loading" : "ready"}
                  settings={mapData?.settings}
                  statuses={statuses?.rows}
                  categories={mapData?.categories}
                  homepageTemplate={mapData?.settings?.homepageTemplate}
                  templateOverride={
                    (systemPages?.rows || []).find(p => p.slug === "home" && p.is_active)?.template
                  }
                />
              }
            />
            {/* DB-driven system page routes */}
            {(systemPages?.rows || [])
              .filter(p => p.is_active && p.slug && p.slug !== "home")
              .map(p => (
                <Route
                  key={p.slug}
                  path={p.slug}
                  element={
                    <Home
                      settings={mapData?.settings}
                      statuses={statuses?.rows}
                      categories={mapData?.categories}
                      homepageTemplate={mapData?.settings?.homepageTemplate}
                      templateOverride={p.template}
                    />
                  }
                />
              ))
            }
            <Route
              path="detail"
              element={
                <Detail
                  settings={mapData?.settings}
                  statuses={statuses?.rows}
                  categories={mapData?.categories}
                />
              }
            />
            <Route path=":groupSlug" element={<Navigate to="home" replace />} />
            <Route path=":groupSlug/*" element={<ProjectDetail />} />
            <Route
              path="internal/home"
              element={
                <ProtectedRoute allowedRoles={["admin", "editor"]}>
                  <InternalProjectDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="admin/datasheet"
              element={
                <ProtectedRoute allowedRoles={["admin", "editor"]}>
                  <DataSheet />
                </ProtectedRoute>
              }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </Suspense>
        </main>

        {/* Intro tutorial modal */}
        <IntroTutorial />
      </div>
    </LocaleContext.Provider>
  );
}
